/** @format */

import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { ThemeProvider } from '../../../../theme';
import VirtualKitForm from './VirtualKitForm';
import VirtualKitList from './VirtualKitList';

import { api } from '../../../../api.ts';

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

function VirtualKitConfigPage() {
  const [configs, setConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formSubmitLoading, setFormSubmitLoading] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 200);

  const loadProducts = useCallback(() => {
    setIsLoading(true);
    setError(null);
    const params = [];
    if (debouncedSearch) {
      params.push(`search=${encodeURIComponent(debouncedSearch)}`);
    }
    params.push(`page=${paginationModel.page + 1}`);
    params.push(`page_size=${paginationModel.pageSize}`);
    const url = `/quotes/v2/admin-virtual-kits/?${params.join('&')}`;
    api
      .get(url)
      .then((response) => {
        setConfigs(response.data.results || []);
        setRowCount(response.data.count || 0);
        setIsLoading(false);
      })
      .catch((err) => {
        const errorMsg =
          err.response?.data?.detail ||
          err.message ||
          'Failed to load configurations.';
        setError(`list-load: ${errorMsg}`);
        console.error('Load configurations error:', err);
        setIsLoading(false);
      });
  }, [paginationModel.page, paginationModel.pageSize, debouncedSearch]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleOpenForm = async (vkConfig = null) => {
    setError(null); // Clear general page error
    if (vkConfig && vkConfig.id) {
      setFormSubmitLoading(true);
      try {
        const response = await api.get(
          `/quotes/v2/admin-virtual-kits/${vkConfig.id}/`,
        );
        setEditingConfig({ ...response.data });
        setIsFormOpen(true);
      } catch (err) {
        setError('Erreur lors du chargement des détails de la configuration.');
      } finally {
        setFormSubmitLoading(false);
      }
    } else {
      setEditingConfig(vkConfig);
      setIsFormOpen(true);
    }
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingConfig(null);
  };

  // onSave in VirtualKitForm will call this
  const handleSaveProduct = async (configData, configId = null) => {
    setFormSubmitLoading(true);
    try {
      let savedConfig;
      if (configId) {
        const response = await api.patch(
          `/quotes/v2/admin-virtual-kits/${configId}/`,
          {},
          configData,
        );
        savedConfig = response.data;
      } else {
        const response = await api.post('/virtual-kit-config/', {}, configData);
        savedConfig = response.data;
      }
      handleCloseForm();
      loadProducts();
      return savedConfig;
    } finally {
      setFormSubmitLoading(false);
    }
  };

  // Filter products by public_name (case-insensitive)
  /* const filteredProducts = products.filter((product) =>
    product.public_name?.toLowerCase().includes(searchQuery.toLowerCase()),
  ); */

  // Inline edit handler for DataGrid
  const handleProcessRowUpdate = async (newRow, oldRow) => {
    try {
      // Only send changed fields
      const changedFields = {};
      if (newRow.is_active !== oldRow.is_active)
        changedFields.is_active = newRow.is_active;
      if (Object.keys(changedFields).length === 0) return oldRow;
      await api.patch(
        `/quotes/v2/admin-virtual-kits/${newRow.id}/`,
        {},
        changedFields,
      );
      // Optionally, reload the list
      loadProducts();
      return { ...oldRow, ...changedFields };
    } catch (err) {
      // Optionally, show error
      setError('Erreur lors de la mise à jour de la configuration.');
      return oldRow;
    }
  };

  return (
    <ThemeProvider>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Typography variant="h4" gutterBottom component="div">
          Interface de configuration des kits virtuels
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              position: 'relative',
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
            >
              Ajouter une nouvelle configuration
            </Button>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Rechercher"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            <Box sx={{ flex: 1 }} />
          </Box>
        </Box>

        <VirtualKitList
          configs={configs}
          isLoading={isLoading}
          onEdit={handleOpenForm}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          rowCount={rowCount}
          onProcessRowUpdate={handleProcessRowUpdate}
        />

        {isFormOpen && (
          <VirtualKitForm
            open={isFormOpen}
            onClose={handleCloseForm}
            onSave={handleSaveProduct}
            config={editingConfig}
            isLoading={formSubmitLoading}
          />
        )}
      </Box>
    </ThemeProvider>
  );
}

export default VirtualKitConfigPage;
